class EvProductGallery extends HTMLElement {
  static get observedAttributes() {
    return ['selected-index'];
  }

  constructor() {
    super();
    this._onThumbClick = this._onThumbClick.bind(this);
    this._onNavClick = this._onNavClick.bind(this);
    this._onVtoClick = this._onVtoClick.bind(this);
    this._suppressObserver = false;
  }

  connectedCallback() {
    this.slidesContainer = this.querySelector('[data-ev-slides]');
    this.slides = Array.from(this.querySelectorAll('[data-ev-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-ev-thumb]'));
    this.navButtons = Array.from(this.querySelectorAll('[data-ev-nav]'));
    this.overlay = this.querySelector('[data-ev-overlay]');
    this.vtoTrigger = this.querySelector('[data-ev-vto-trigger]');

    this._scheduleVtoWiring();

    if (this.vtoTrigger) this.vtoTrigger.addEventListener('click', this._onVtoClick);

    if (!this.slidesContainer || this.slides.length === 0) return;

    this.thumbs.forEach((thumb) => thumb.addEventListener('click', this._onThumbClick));
    this.navButtons.forEach((btn) => btn.addEventListener('click', this._onNavClick));

    this._setupObserver();

    const initial = this._readIndexAttr();
    this._applySelection(initial, { smooth: false });
  }

  _onNavClick(event) {
    const direction = event.currentTarget.dataset.evNav;
    const current = this._readIndexAttr();
    const delta = direction === 'next' ? 1 : -1;
    this._applySelection(current + delta, { smooth: true });
  }

  _updateNavState(index) {
    if (!this.navButtons || this.navButtons.length === 0) return;
    const total = this.slides.length;
    this.navButtons.forEach((btn) => {
      const isPrev = btn.dataset.evNav === 'prev';
      btn.disabled = isPrev ? index <= 0 : index >= total - 1;
    });
  }

  _scheduleVtoWiring() {
    const wire = () => this._wireVtoTrigger();
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', wire, { once: true });
    } else {
      wire();
    }
  }

  _wireVtoTrigger() {
    if (!this.vtoTrigger) return;
    const target = this._findVtoTarget();
    if (target) {
      this.vtoTrigger.hidden = false;
    } else {
      this.vtoTrigger.hidden = true;
    }
  }

  _findVtoTarget() {
    const sectionId = this.dataset.sectionId;
    const root = sectionId ? document.getElementById('MainProduct-' + sectionId) : document;
    if (!root) return null;
    const portal = root.querySelector('[data-ev-gallery-portal]');
    if (!portal) return null;
    return portal.querySelector('button, a, [role="button"]') || null;
  }

  _onVtoClick(event) {
    event.preventDefault();
    const target = this._findVtoTarget();
    if (!target) return;
    if (typeof target.click === 'function') {
      target.click();
      return;
    }
    target.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
  }

  disconnectedCallback() {
    this.thumbs?.forEach((thumb) => thumb.removeEventListener('click', this._onThumbClick));
    this.navButtons?.forEach((btn) => btn.removeEventListener('click', this._onNavClick));
    this.vtoTrigger?.removeEventListener('click', this._onVtoClick);
    this.observer?.disconnect();
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (name !== 'selected-index' || oldValue === newValue) return;
    if (!this.isConnected || !this.slides) return;
    const idx = this._clamp(Number(newValue) || 0);
    this._updateThumbsState(idx);
    this._updateNavState(idx);
    this._scrollToSlide(idx, true);
  }

  _onThumbClick(event) {
    const target = event.currentTarget;
    const idx = Number(target.dataset.index);
    if (Number.isNaN(idx)) return;
    this._applySelection(idx, { smooth: true });
  }

  _applySelection(index, { smooth }) {
    const idx = this._clamp(index);
    this._updateThumbsState(idx);
    this._updateNavState(idx);
    this._scrollToSlide(idx, smooth);
    if (Number(this.getAttribute('selected-index')) !== idx) {
      this._suppressAttrCallback = true;
      this.setAttribute('selected-index', String(idx));
      this._suppressAttrCallback = false;
    }
    this.dispatchEvent(
      new CustomEvent('ev-gallery:change', {
        detail: { index: idx, mediaId: this.slides[idx]?.dataset.mediaId || null },
        bubbles: true
      })
    );
  }

  _updateThumbsState(index) {
    if (!this.thumbs) return;
    this.thumbs.forEach((thumb) => {
      const isActive = Number(thumb.dataset.index) === index;
      thumb.setAttribute('aria-selected', String(isActive));
      thumb.tabIndex = isActive ? 0 : -1;
    });
  }

  _scrollToSlide(index, smooth) {
    const slide = this.slides[index];
    if (!slide || !this.slidesContainer) return;

    this._suppressObserver = true;
    const target = slide.offsetLeft - this.slidesContainer.offsetLeft;
    this.slidesContainer.scrollTo({
      left: target,
      behavior: smooth ? 'smooth' : 'auto'
    });

    clearTimeout(this._suppressTimer);
    this._suppressTimer = setTimeout(() => {
      this._suppressObserver = false;
    }, smooth ? 450 : 60);
  }

  _setupObserver() {
    if (typeof IntersectionObserver === 'undefined') return;

    this.observer = new IntersectionObserver(
      (entries) => {
        if (this._suppressObserver) return;
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (!visible) return;
        const idx = Number(visible.target.dataset.index);
        if (Number.isNaN(idx)) return;
        if (Number(this.getAttribute('selected-index')) === idx) return;
        this._updateThumbsState(idx);
        this._updateNavState(idx);
        this._suppressAttrCallback = true;
        this.setAttribute('selected-index', String(idx));
        this._suppressAttrCallback = false;
      },
      {
        root: this.slidesContainer,
        threshold: [0.6, 0.9]
      }
    );

    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  _readIndexAttr() {
    const raw = Number(this.getAttribute('selected-index'));
    return this._clamp(Number.isFinite(raw) ? raw : 0);
  }

  _clamp(index) {
    if (!this.slides || this.slides.length === 0) return 0;
    return Math.max(0, Math.min(this.slides.length - 1, index));
  }
}

if (!customElements.get('ev-product-gallery')) {
  customElements.define('ev-product-gallery', EvProductGallery);
}
